/**
 * ActivityCard Component
 *
 * A card component for displaying activity information with an image, title, professional name,
 * location type, booking count, and starting price.
 */
import { MapPin, Video } from 'lucide-react';

import { interceptLinkClick } from '../../../../lib/linkClick';
import { cn, formatStartingPrice } from '../../../../lib/utils';
import { ImageWithFallback } from '../../../common/imageWithFallback/ImageWithFallback';
import BriefcaseOutline from '../../../icons/BriefcaseOutline';
import CoinLinear from '../../../icons/CoinLinear';
import HomeOutline from '../../../icons/HomeOutline';
import { type LocationKind, LocationKindChips } from '../locationKindChips';
import { UnavailableBadge } from '../unavailableBadge';
import { cardHubLabel, cardLocationText, defaultAtHubText } from './cardLocation';

export interface ActivityCardProps {
  /**
   * Reference to the main activity image
   */
  imageUrl: string;

  /**
   * Title of the activity
   */
  title: string;

  /**
   * Name of the professional offering this activity
   */
  professionalName: string;

  /**
   * Whether the activity happens at client's home
   */
  isAtHome?: boolean;

  /**
   * Whether this is an online (visio) booking. When true, a violet "online"
   * chip is shown instead of the at-home / at-venue location row.
   */
  isOnline?: boolean;

  /**
   * The activity's real delivery-mode MIX (venue / at_home / online). When it
   * holds 2+ kinds the location row renders one compact chip per kind instead
   * of the single `isAtHome`/`isOnline` indicator. A single-kind (or absent)
   * value keeps the legacy single-indicator look (which can show a city).
   */
  locationKinds?: LocationKind[];

  /**
   * Short location label for venue activities (typically the city). Shown in
   * place of `atVenueText` when present. Ignored when `isAtHome` is true.
   */
  city?: string;

  /**
   * The Hub the activity runs at: its venue reads `atHubText` ("At Studio Rivoli") in place of the
   * city, and on the venue chip of a mixed activity.
   */
  hub?: { name: string } | null;

  /**
   * Total number of bookings for this activity since last year
   */
  bookingsSinceLastYear?: number;

  /**
   * Starting price for the activity.
   *
   * - `null` / `undefined` → activity has no priced booking yet; the price
   *   block is hidden entirely (must NOT be shown as "0 €").
   * - `0` → genuinely free; shows `freeText`.
   * - `> 0` → formatted amount with the `onwardsText` prefix.
   */
  startingPrice?: number | null;

  /**
   * When set, the activity cannot be booked right now: a muted badge with this
   * (translated) text sits on the image, e.g. "No availability yet".
   */
  unavailableText?: string;

  /**
   * Optional click handler. When `href` is also set, it runs on a plain left
   * click (and native navigation is prevented); modified clicks follow `href`.
   */
  onClick?: () => void;

  /**
   * URL of the activity page. When set, the title becomes a real link stretched
   * over the whole card, so the card is crawlable and opens in a new tab on
   * cmd/ctrl/middle click.
   */
  href?: string;

  /**
   * Optional CSS class name
   */
  className?: string;

  /**
   * Translated text for "At Home" location
   * @default "At Home"
   */
  atHomeText?: string;

  /**
   * Translated text for "At Venue" location
   * @default "At Venue"
   */
  atVenueText?: string;

  /**
   * Translated text for an online (visio) booking
   * @default "Online"
   */
  onlineText?: string;

  /**
   * Translated venue of an activity at a Hub
   * @default (hubName) => `At ${hubName}`
   */
  atHubText?: (hubName: string) => string;

  /**
   * Translated prefix for bookings count text
   * @default ""
   */
  bookingsInYearPrefix?: string;

  /**
   * Translated middle text between count and year for bookings text
   * @default " Bookings since "
   */
  bookingsInYearMiddle?: string;

  /**
   * Translated suffix for bookings count text
   * @default ""
   */
  bookingsInYearSuffix?: string;

  /**
   * Translated text for price suffix ("onwards")
   * @default "onwards"
   */
  onwardsText?: string;

  /**
   * Translated label shown instead of "0 €" for free bookings
   * @default "Free"
   */
  freeText?: string;

  /**
   * Locale for currency formatting (e.g. 'fr-FR', 'en-US')
   * @default 'fr-FR'
   */
  locale?: string;
}

/**
 * ActivityCard component for displaying activity information
 *
 * @example
 * ```tsx
 * <ActivityCard
 *   imageUrl="https://example.com/image.jpg"
 *   title="Yoga for Beginners"
 *   professionalName="Jane Doe"
 *   isAtHome={true}
 *   currentYearBookingsCount={22}
 *   startingPrice={40}
 *   onClick={() => console.log('Activity clicked')}
 * />
 * ```
 */
export const ActivityCard = ({
  imageUrl,
  title,
  professionalName,
  isAtHome = false,
  isOnline = false,
  locationKinds,
  city,
  hub,
  bookingsSinceLastYear,
  startingPrice,
  unavailableText,
  onClick,
  href,
  className,
  atHomeText = 'At Home',
  atVenueText = 'At Venue',
  onlineText = 'Online',
  atHubText = defaultAtHubText,
  bookingsInYearPrefix = '',
  bookingsInYearMiddle = ' Bookings since ',
  bookingsInYearSuffix = '',
  onwardsText = 'onwards',
  freeText = 'Free',
  locale = 'fr-FR'
}: ActivityCardProps) => {
  const formattedPrice = formatStartingPrice(startingPrice, { locale, onwardsText, freeText });
  const venueLabel = cardHubLabel(hub, atHubText);

  // A mixed activity (2+ delivery kinds) renders one compact chip per kind;
  // otherwise the legacy single indicator (which can show a city for venue).
  const showKindMix = (locationKinds?.length ?? 0) >= 2;

  return (
    <div
      className={cn(
        'w-48 bg-card rounded-xl border border-border inline-flex flex-col justify-start items-start overflow-hidden cursor-pointer shadow-sm hover:shadow-md transition-shadow',
        href && 'relative',
        className
      )}
      onClick={href ? undefined : onClick}
    >
      {/* Image section */}
      <div className="self-stretch h-40 relative">
        <ImageWithFallback src={imageUrl} alt={title} className="w-full h-full object-cover" sizes="192px" />
        {unavailableText && <UnavailableBadge text={unavailableText} className="absolute top-2 left-2" />}
      </div>

      {/* Content section */}
      <div className="self-stretch p-3 flex flex-col justify-start items-start gap-2">
        {/* Title and professional name */}
        <div className="self-stretch flex flex-col justify-start items-start">
          <div className="self-stretch text-foreground text-base font-semibold leading-snug truncate" title={title}>
            {href ? (
              <a
                href={href}
                draggable={false}
                onClick={(e) => interceptLinkClick(e, onClick)}
                className="after:absolute after:inset-0 after:content-[''] focus-visible:outline-none focus-visible:after:rounded-xl focus-visible:after:ring-2 focus-visible:after:ring-inset focus-visible:after:ring-ring"
              >
                {title}
              </a>
            ) : (
              title
            )}
          </div>
          <div className="self-stretch text-muted-foreground text-xs font-medium leading-none">{professionalName}</div>
        </div>

        {/* Activity details */}
        <div className="self-stretch flex flex-col justify-center items-start gap-[5px]">
          {/* Location (Online / At Home / At Venue): chips for a mixed activity. */}
          {showKindMix ? (
            <LocationKindChips
              kinds={locationKinds ?? []}
              labels={{ venue: venueLabel || atVenueText, at_home: atHomeText, online: onlineText }}
              size="xs"
              className="self-stretch"
            />
          ) : (
            <div className="self-stretch inline-flex justify-start items-center gap-2">
              {isOnline ? (
                <Video className="w-4 h-4 text-violet-600" />
              ) : isAtHome ? (
                <HomeOutline className="w-4 h-4 text-muted-foreground" />
              ) : (
                <MapPin className="w-4 h-4 text-muted-foreground" />
              )}
              <div className="flex justify-start items-center gap-1 min-w-0">
                <div
                  className={cn(
                    'text-xs font-medium leading-none truncate',
                    isOnline ? 'text-violet-700' : 'text-muted-foreground'
                  )}
                >
                  {cardLocationText(
                    { isOnline, isAtHome, hub, city },
                    { online: onlineText, atHome: atHomeText, atVenue: atVenueText, atHub: atHubText }
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Bookings count - only show if more than 5 */}
          {bookingsSinceLastYear != null && bookingsSinceLastYear > 5 && (
            <div className="self-stretch inline-flex justify-start items-center gap-2">
              <BriefcaseOutline className="w-4 h-4 text-muted-foreground" />
              <div className="text-muted-foreground text-xs font-normal">
                {bookingsInYearPrefix}
                {bookingsSinceLastYear}
                {bookingsInYearMiddle}
                {new Date().getFullYear() - 1}
                {bookingsInYearSuffix}
              </div>
            </div>
          )}

          {/* Price - hidden when there is no priced booking yet */}
          {formattedPrice && (
            <div className="self-stretch inline-flex justify-start items-center gap-2">
              <CoinLinear className="w-4 h-4 text-muted-foreground" />
              <div>
                {formattedPrice.pricePrefix && (
                  <span className="text-muted-foreground text-xs font-normal">{formattedPrice.pricePrefix} </span>
                )}
                <span className="text-primary text-sm font-semibold leading-tight">{formattedPrice.price}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
